<script setup lang="ts">
/* eslint-disable max-lines */
import { useAerialImageAnnotationModal } from '@/composables/annotation/useAerialImageAnnotationModal'
import CustomButton from '@/components/annotation/button/CustomButton.vue'
import { useDisasterStore } from '@/stores/disasterStore'
import { runClassificationAnnotationApi } from '@/api/runClassificationAnnotation/runClassificationAnnotationApiGwFuncs'
import type { RunClassificationAnnotationRequestParam } from '@/types/api/runClassificationAnnotation/runClassificationAnnotationTypes'
import { GlobalError } from '@/utils/globalError/globalError'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { useLoadingView } from '@/features/miiaFw/composables/useLoadingView'

const { setLoadingView } = useLoadingView()
const disasterStore = useDisasterStore()
const {
  isOpen,
  totalCollapseImages,
  notTotallyDestroyedImages,
  selectedImages,
  close,
  toggleSelect,
  getAnnotations
} = useAerialImageAnnotationModal()

/** スキップボタン押下時の処理 */
const onSkip = (): void => {
  close()
}

/** 選択完了ボタン押下時の処理 */
const onSelectComplete = async (): Promise<void> => {
  if (!disasterStore.govCode || !disasterStore.disasterId) return
  // 分類アルゴリズム（アノテーション）起動API 実行
  const args: RunClassificationAnnotationRequestParam = {
    govCode: disasterStore.govCode,
    disasterId: disasterStore.disasterId,
    annotations: getAnnotations()
  }
  const { callRun } = runClassificationAnnotationApi()
  await callRun(args)
    .then(res => {
      if (res.error !== undefined) {
        throw new GlobalError([ERROR_CODES.CLASSIFICATION_ANNOTATION_RUN_FAILED])
      }
    })
    .catch(() => {
      throw new GlobalError([ERROR_CODES.CLASSIFICATION_ANNOTATION_RUN_FAILED])
    })
    .finally(() => {
      setLoadingView(false)
    })
  close()
}
</script>

<template>
  <div v-if="isOpen" class="modal-wrapper">
    <div class="modal-content" role="dialog" aria-modal="true">
      <header>
        <div class="modal-title">空撮画像へのタグ付け</div>
      </header>

      <section class="body-section">
        <div class="description">
          以下の「全壊」「非全壊」に分類された画像から分類が<b><u>正しくないものを選択</u></b
          >し、「選択を完了する」を押下してください。<br />
          すべての画像の分類が正しい場合は、未選択のまま「選択を完了する」を押下してください。<br />
          この操作をすることで倒壊判定の精度が向上します。<br />
          再判定を行わない場合は「スキップする」を押下してください。
        </div>

        <!-- 画像群表示部分 -->
        <div class="image-selection-container">
          <div class="image-group all-damaged">
            <div class="all-damaged-title">全壊</div>
            <div class="image-list">
              <div
                v-for="item in totalCollapseImages"
                :key="item.id"
                class="image-wrapper"
                @click="toggleSelect(item)"
              >
                <img
                  :src="item.objectUrl"
                  alt="全壊画像"
                  :class="{ selected: selectedImages.has(item.id) }"
                />
                <font-awesome-icon
                  v-if="selectedImages.has(item.id)"
                  icon="fa-check-circle"
                  class="check-icon"
                />
              </div>
            </div>
          </div>

          <div class="image-group not-all-damaged">
            <div class="not-all-damaged-title">非全壊</div>
            <div class="image-list">
              <div
                v-for="item in notTotallyDestroyedImages"
                :key="item.id"
                class="image-wrapper"
                @click="toggleSelect(item)"
              >
                <img
                  :src="item.objectUrl"
                  alt="非全壊画像"
                  :class="{ selected: selectedImages.has(item.id) }"
                />
                <font-awesome-icon
                  v-if="selectedImages.has(item.id)"
                  icon="fa-check-circle"
                  class="check-icon"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer>
        <CustomButton variant="outline-primary" @click="onSkip">スキップする</CustomButton>
        <CustomButton variant="primary" @click="onSelectComplete">選択を完了する</CustomButton>
      </footer>
    </div>
  </div>
</template>

<style scoped>
/* オーバーレイ部分 */
.modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
  overflow-y: auto;
  padding: 1rem;
  box-sizing: border-box;
}

/* モーダル本体 */
.modal-content {
  border-radius: 6px;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.3);
  max-width: 1200px;
  width: 100%;
  height: 90vh;
  display: flex;
  flex-direction: column;
  outline: none;
  border-radius: 4px;
}

header {
  background-color: #ddd;
  padding: 0.5rem;
  border-bottom: 1px solid #ccc;
  border-radius: 4px 4px 0 0;
}

.modal-title {
  padding-left: 0.5rem;
  font-size: 25px;
}

.body-section {
  background-color: #f9f9f9;
  padding: 1rem 1.25rem;
  flex: 1 1 auto;
  overflow-y: auto;
}

footer {
  border-top: none;
  background-color: #f9f9f9;
  padding: 1rem 1.25rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  flex-shrink: 0;
  border-radius: 0 0 4px 4px;
}

.description {
  font-size: 16px;
  margin-bottom: 1rem;
}

/* 画像グループ全体のレイアウト */
.image-selection-container {
  display: flex;
  justify-content: center;
  gap: 24px;
  width: 100%;
  height: 85%;
  box-sizing: border-box;
  flex-wrap: wrap;
  min-height: 500px;
}

/* 各グループのスタイル */
.image-group {
  flex: 1 1 48%;
  max-width: 48%;
  border: 1px solid #ccc;
  padding: 0 0 8px 0;
  box-sizing: border-box;
  border-radius: 4px;
  background-color: white;
}

.all-damaged-title {
  margin: 0 12px;
  font-size: 18px;
  font-weight: bold;
  color: white;
  align-content: center;
  background-color: #994545;
  line-height: 1.4;
  height: auto;
  border-radius: 3px 3px 0 0;
  border: 1px solid #ccc;
  padding: 8px 12px;
  margin: 0;
  text-align: center;
}

.not-all-damaged-title {
  margin: 0 12px;
  font-size: 18px;
  font-weight: bold;
  color: white;
  align-content: center;
  background-color: #60d1a6;
  line-height: 1.4;
  height: auto;
  border-radius: 4px 4px 0 0;
  border: 1px solid #ccc;
  padding: 8px 12px;
  margin: 0;
  text-align: center;
}

/* 画像リスト */
.image-list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin: 12px;
  padding: 0;
  align-items: start;
}

.image-wrapper {
  position: relative;
  cursor: pointer;
  display: block;
  width: 100%;
  height: 120px;
}

.image-wrapper img {
  width: 100%;
  height: 100%;
  min-width: 100%;
  min-height: 100%;
  object-fit: contain;
  border: 1px solid #aaa;
  border-radius: 2px;
  transition:
    box-shadow 0.2s ease,
    border-color 0.2s ease;
}

.image-wrapper img:hover {
  box-shadow: 0 0 8px rgba(0, 82, 204, 0.7);
}

/* 選択時 */
.all-damaged .image-list img.selected {
  border: 3px solid #994545;
}

.not-all-damaged .image-list img.selected {
  border: 3px solid #60d1a6;
}

/* チェックマーク */
.check-icon {
  position: absolute;
  top: 4px;
  right: 4px;
  font-size: 20px;
  background-color: white;
  border-radius: 50%;
  padding: 2px;
  pointer-events: none;
  z-index: 10;
}

.all-damaged .image-list img.selected + .check-icon {
  color: #994545;
}

.not-all-damaged .image-list img.selected + .check-icon {
  color: #60d1a6;
}
</style>
